import { useCallback, useRef, useEffect } from 'react';
import { auditLogsApi } from '../services/api';
import {
  AuditAction,
  AuditMetadata,
  ViewSource,
  TTSSection,
  TTSProvider,
} from '../types';

/**
 * Hook for logging audit events on interpretations.
 *
 * Features:
 * - Fire-and-forget: Events are sent asynchronously without blocking UI
 * - Debouncing: Frequent events (like STEP_CHANGE) are debounced
 * - Auto VIEW on mount: Automatically logs VIEW when component mounts
 * - Auto LEAVE on unmount: Logs LEAVE with time spent when component unmounts
 *
 * @param interpretationId - The interpretation being audited
 * @param patientId - Optional patient ID for context
 * @param viewSource - Source of the VIEW event (list, dashboard, direct)
 */
export function useAuditLogger(
  interpretationId: string | undefined,
  patientId?: string,
  viewSource: ViewSource = 'direct'
) {
  const mountTimeRef = useRef<number>(Date.now());
  const ttsStartTimeRef = useRef<number | null>(null);
  const editStartTimeRef = useRef<number | null>(null);
  const stepChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastStepRef = useRef<number | null>(null);
  const hasLoggedViewRef = useRef(false);

  /**
   * Log an audit event
   */
  const logEvent = useCallback(
    (action: AuditAction, metadata?: AuditMetadata) => {
      if (!interpretationId) return;

      // Fire-and-forget
      auditLogsApi.recordEvent(interpretationId, action, metadata, patientId);
    },
    [interpretationId, patientId]
  );

  /**
   * Log VIEW event (called automatically on mount)
   */
  const logView = useCallback(() => {
    if (hasLoggedViewRef.current) return;
    hasLoggedViewRef.current = true;
    logEvent('VIEW', { source: viewSource });
  }, [logEvent, viewSource]);

  /**
   * Log TTS start
   */
  const logTTSStart = useCallback(
    (section: TTSSection, provider: TTSProvider) => {
      ttsStartTimeRef.current = Date.now();
      logEvent('TTS_START', { section, provider });
    },
    [logEvent]
  );

  /**
   * Log TTS stop
   */
  const logTTSStop = useCallback(
    (completed: boolean) => {
      const startTime = ttsStartTimeRef.current;
      const durationSeconds = startTime
        ? Math.round((Date.now() - startTime) / 1000)
        : 0;
      ttsStartTimeRef.current = null;
      logEvent('TTS_STOP', { duration_seconds: durationSeconds, completed });
    },
    [logEvent]
  );

  /**
   * Log regeneration request
   */
  const logRegenerate = useCallback(
    (category?: string, reason?: string) => {
      logEvent('REGENERATE_REQUEST', { category, reason });
    },
    [logEvent]
  );

  /**
   * Log edit start
   */
  const logEditStart = useCallback(() => {
    editStartTimeRef.current = Date.now();
    logEvent('EDIT_START', {});
  }, [logEvent]);

  /**
   * Log edit cancel
   */
  const logEditCancel = useCallback(() => {
    const startTime = editStartTimeRef.current;
    const durationSeconds = startTime
      ? Math.round((Date.now() - startTime) / 1000)
      : 0;
    editStartTimeRef.current = null;
    logEvent('EDIT_CANCEL', { edit_duration_seconds: durationSeconds });
  }, [logEvent]);

  /**
   * Log approval without changes
   */
  const logApprove = useCallback(() => {
    editStartTimeRef.current = null;
    logEvent('APPROVE', {});
  }, [logEvent]);

  /**
   * Log approval with edits
   */
  const logApproveWithEdit = useCallback(
    (modificationReason?: string, charsChanged?: number) => {
      editStartTimeRef.current = null;
      logEvent('APPROVE_WITH_EDIT', {
        modification_reason: modificationReason,
        chars_changed: charsChanged,
      });
    },
    [logEvent]
  );

  /**
   * Log step change (debounced to avoid spam when navigating quickly)
   */
  const logStepChange = useCallback(
    (fromStep: number, toStep: number) => {
      // Clear any pending step change
      if (stepChangeTimeoutRef.current) {
        clearTimeout(stepChangeTimeoutRef.current);
      }

      // Debounce: only log if user stays on step for 500ms
      stepChangeTimeoutRef.current = setTimeout(() => {
        const from = lastStepRef.current ?? fromStep;
        lastStepRef.current = toStep;
        logEvent('STEP_CHANGE', { from_step: from, to_step: toStep });
      }, 500);
    },
    [logEvent]
  );

  /**
   * Log leaving the page
   */
  const logLeave = useCallback(() => {
    const timeOnPageSeconds = Math.round(
      (Date.now() - mountTimeRef.current) / 1000
    );
    logEvent('LEAVE', { time_on_page_seconds: timeOnPageSeconds });
  }, [logEvent]);

  // Auto-log VIEW on mount
  useEffect(() => {
    if (interpretationId) {
      logView();
    }
  }, [interpretationId, logView]);

  // Store current values in refs for cleanup access
  const interpretationIdRef = useRef(interpretationId);
  const patientIdRef = useRef(patientId);

  useEffect(() => {
    interpretationIdRef.current = interpretationId;
    patientIdRef.current = patientId;
  }, [interpretationId, patientId]);

  // Auto-log LEAVE on unmount only (empty dependency array)
  useEffect(() => {
    return () => {
      // Clear any pending debounced events
      if (stepChangeTimeoutRef.current) {
        clearTimeout(stepChangeTimeoutRef.current);
      }

      // Only log LEAVE if we logged VIEW (user actually viewed the interpretation)
      if (hasLoggedViewRef.current && interpretationIdRef.current) {
        const timeOnPageSeconds = Math.round(
          (Date.now() - mountTimeRef.current) / 1000
        );

        // Fire-and-forget the leave event
        auditLogsApi.recordEvent(
          interpretationIdRef.current,
          'LEAVE',
          { time_on_page_seconds: timeOnPageSeconds },
          patientIdRef.current
        );
      }
    };
  }, []);

  return {
    logView,
    logTTSStart,
    logTTSStop,
    logRegenerate,
    logEditStart,
    logEditCancel,
    logApprove,
    logApproveWithEdit,
    logStepChange,
    logLeave,
    logEvent, // For custom events
  };
}
